/**
 * Column Definitions for Portfolio Table
 *
 * Defines all available columns that can be used in portfolio table views.
 * Each config selects which columns to display from this library.
 */

import React from "react";
import { ColumnDefinition, PortfolioRow } from "./columnTypes";
import {
    formatCurrency,
    formatFxRate,
    formatNullable,
    formatNumber,
    formatPercent,
    formatPnl,
    formatPrice,
    formatTradeQty,
    formatUsd,
} from "./formatters";

/**
 * All available column definitions.
 * Configs reference these by ID to build their column list.
 */
export const columnDefinitions: Record<string, ColumnDefinition> = {
  // ============================================================================
  // Core Columns (11)
  // ============================================================================

  symbol: {
    id: "symbol",
    label: "Symbol",
    accessor: (row: PortfolioRow) => row.symbol,
    sortable: true,
    align: "left",
    dataSource: "live",
  },

  currency: {
    id: "currency",
    label: "C",
    accessor: (row: PortfolioRow) => row.currency,
    formatter: value => formatNullable(value as string),
    sortable: true,
    align: "center",
    width: "35px",
    filterable: true,
    filterOptions: (rows: PortfolioRow[]) => {
      const currencies = new Set<string>();
      rows.forEach(row => {
        if (row.currency) currencies.add(row.currency);
      });
      return Array.from(currencies).sort();
    },
    dataSource: "live",
  },

  shares: {
    id: "shares",
    label: "Shares",
    accessor: (row: PortfolioRow) => row.shares,
    formatter: value => formatNumber(value as number, 3),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.shares,
    align: "right",
    dataSource: "live",
  },

  qty: {
    id: "qty",
    label: "QTY",
    accessor: (row: PortfolioRow) => row.shares,
    formatter: value => formatNumber(value as number),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.shares,
    align: "right",
    dataSource: "live",
  },

  avgCost: {
    id: "avgCost",
    label: "Avg Cost",
    accessor: (row: PortfolioRow) => row.avgCost,
    formatter: (value, row) => formatPrice(value as number, row.currency),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.avgCost,
    align: "right",
    dataSource: "live",
  },

  costBasis: {
    id: "costBasis",
    label: "Cost Basis",
    accessor: (row: PortfolioRow) => row.shares * row.avgCost,
    formatter: (value, row) => formatCurrency(value as number, row.currency),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.shares * row.avgCost,
    align: "right",
    dataSource: "live",
  },

  costBasisLocal: {
    id: "costBasisLocal",
    label: "Cost Basis (Local)",
    accessor: (row: PortfolioRow) => row.shares * row.avgCost,
    formatter: (value, row) => formatCurrency(value as number, row.currency),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.shares * row.avgCost,
    align: "right",
    dataSource: "live",
  },

  mktPrice: {
    id: "mktPrice",
    label: "Price",
    accessor: (row: PortfolioRow) => row.mktPrice,
    formatter: (value, row) => formatPrice(value as number, row.currency, 3),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.mktPrice,
    align: "right",
    dataSource: "live",
  },

  priceUsd: {
    id: "priceUsd",
    label: "Price (USD)",
    accessor: (row: PortfolioRow) => row.priceUsd,
    formatter: value => formatUsd(value as number | null),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.priceUsd,
    align: "right",
    dataSource: "live",
  },

  mktValue: {
    id: "mktValue",
    label: "Mkt Value",
    accessor: (row: PortfolioRow) => row.mktValue,
    formatter: (value, row) => formatCurrency(value as number, row.currency),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.mktValue,
    align: "right",
    dataSource: "live",
  },

  mktValueLocal: {
    id: "mktValueLocal",
    label: "Mkt Value (Local)",
    accessor: (row: PortfolioRow) => row.mktValue,
    formatter: (value, row) => formatCurrency(value as number, row.currency),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.mktValue,
    align: "right",
    dataSource: "live",
  },

  mktValueUsd: {
    id: "mktValueUsd",
    label: "Mkt Value (USD)",
    accessor: (row: PortfolioRow) => row.mktValueUsd,
    formatter: value => formatUsd(value as number),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.mktValueUsd,
    align: "right",
    dataSource: "live",
  },

  realizedPnl: {
    id: "realizedPnl",
    label: "Realized P&L",
    accessor: (row: PortfolioRow) => row.realizedPnl,
    formatter: (value, row) => formatPnl(value as number, row.currency),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.realizedPnl,
    align: "right",
    dataSource: "live",
  },

  unrealizedPnl: {
    id: "unrealizedPnl",
    label: "Unrealized P&L",
    accessor: (row: PortfolioRow) => row.unrealizedPnl,
    formatter: (value, row) => formatPnl(value as number, row.currency),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.unrealizedPnl,
    align: "right",
    dataSource: "live",
  },

  unrealizedPnlUsd: {
    id: "unrealizedPnlUsd",
    label: "Unrealized P&L (USD)",
    accessor: (row: PortfolioRow) => row.unrealizedPnlUsd,
    formatter: value => {
      const numValue = Number(value) || 0;
      const sign = numValue >= 0 ? "+" : "-";
      const absValue = Math.abs(numValue).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${sign}$${absValue}`;
    },
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.unrealizedPnlUsd,
    align: "right",
    dataSource: "live",
  },

  group: {
    id: "group",
    label: "Group",
    accessor: (row: PortfolioRow) => row.group,
    formatter: (value) => {
      const str = value as string | null;
      if (!str) return "-";
      return React.createElement(
        "span",
        { className: "block max-w-[180px] truncate", title: str },
        str,
      );
    },
    sortable: true,
    align: "left",
    filterable: true,
    filterOptions: (rows: PortfolioRow[]) => {
      const groups = new Set<string>();
      rows.forEach(row => {
        if (row.group) groups.add(row.group);
      });
      return Array.from(groups).sort();
    },
    dataSource: "live",
  },

  sector: {
    id: "sector",
    label: "Sector",
    accessor: (row: PortfolioRow) => row.sector,
    formatter: value => formatNullable(value as string),
    sortable: true,
    align: "left",
    filterable: true,
    filterOptions: (rows: PortfolioRow[]) => {
      const sectors = new Set<string>();
      rows.forEach(row => {
        if (row.sector) sectors.add(row.sector);
      });
      return Array.from(sectors).sort();
    },
    dataSource: "live",
  },

  conId: {
    id: "conId",
    label: "ConId",
    accessor: (row: PortfolioRow) => row.conId,
    formatter: value => String(value),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.conId,
    align: "right",
    dataSource: "live",
  },

  excluded: {
    id: "excluded",
    label: "Ex",
    accessor: (row: PortfolioRow) => (row.exclusionReasons?.includes("manual") ? "Y" : "-"),
    formatter: value => (value === "Y" ? "Y" : "-"),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => (row.exclusionReasons?.includes("manual") ? 1 : 0),
    align: "center",
    width: "30px",
    tooltip: "Contract is excluded from trading for this user",
    filterable: true,
    filterOptions: () => ["Y", "-"],
    dataSource: "live",
  },

  issues: {
    id: "issues",
    label: "!",
    accessor: (row: PortfolioRow) => {
      const issues: string[] = [];
      if (row.exclusionReasons?.includes("zero_price")) issues.push("P");
      if (row.exclusionReasons?.includes("invalid_exchange")) issues.push("X");
      if (row.exclusionReasons?.includes("retirement_short")) issues.push("R");
      return issues.length > 0 ? issues.join("") : "-";
    },
    formatter: (value) => {
      const v = String(value ?? "-");
      return v === "-" ? "-" : v;
    },
    sortable: true,
    sortAccessor: (row: PortfolioRow) => {
      let score = 0;
      if (row.exclusionReasons?.includes("zero_price")) score += 4;
      if (row.exclusionReasons?.includes("invalid_exchange")) score += 2;
      if (row.exclusionReasons?.includes("retirement_short")) score += 1;
      return score;
    },
    align: "center",
    width: "30px",
    tooltip: "Row issues: P = no valid price, X = invalid exchange (VALUE), R = retirement account (no shorts)",
    filterable: true,
    filterOptions: () => ["P", "X", "R", "PX", "PR", "XR", "PXR", "-"],
    dataSource: "live",
  },

  // ============================================================================
  // Expanded Column (1) - shown when user expands table
  // ============================================================================

  secType: {
    id: "secType",
    label: "Ins",
    accessor: (row: PortfolioRow) => row.secType,
    sortable: true,
    align: "center",
    width: "40px",
    filterable: true,
    filterOptions: (rows: PortfolioRow[]) => {
      const types = new Set<string>();
      rows.forEach(row => {
        if (row.secType) types.add(row.secType);
      });
      return Array.from(types).sort();
    },
    dataSource: "live",
  },

  // ============================================================================
  // Diff Columns (6) - shown in rebalance view
  // ============================================================================

  currentWeightPct: {
    id: "currentWeightPct",
    label: "% Acct",
    accessor: (row: PortfolioRow) => row.currentWeightPct,
    formatter: (value, row: PortfolioRow) => {
      if (value !== null && value !== undefined) return formatPercent(value as number, 3);
      if (row.shares !== 0 && row.priceUsd != null) {
        return React.createElement("span", { className: "text-red-500", title: "Weight unavailable" }, "ERR");
      }
      return "-";
    },
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.currentWeightPct ?? 0,
    align: "right",
    tooltip: "Current % of Account",
    isDiffColumn: true,
    dataSource: "live",
  },

  targetWeightPct: {
    id: "targetWeightPct",
    label: "Tgt %",
    accessor: (row: PortfolioRow) => row.targetWeightPct,
    formatter: value => (value !== null && value !== undefined ? formatPercent(value as number, 3) : "-"),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.targetWeightPct ?? 0,
    align: "right",
    isDiffColumn: true,
    filterable: true,
    filterMode: "nonzero",
    dataSource: "stored",
  },

  weightDeviation: {
    id: "weightDeviation",
    label: "Dev %",
    accessor: (row: PortfolioRow) => {
      const tgt = row.targetWeightPct;
      const act = row.currentWeightPct;
      if (tgt == null && act == null) return null;
      if (act == null && row.shares !== 0) return null;
      return (tgt ?? 0) - (act ?? 0);
    },
    formatter: (value, row: PortfolioRow) => {
      if (value == null) {
        if (row.currentWeightPct == null && row.shares !== 0 && row.priceUsd != null) {
          return React.createElement(
            "span",
            { className: "text-red-500", title: "Actual weight unavailable" },
            "ERR",
          );
        }
        return "-";
      }
      const num = value as number;
      if (num === 0) return "0.000%";
      const sign = num > 0 ? "+" : "";
      const color = num > 0 ? "text-green-400" : "text-red-400";
      return React.createElement("span", { className: color }, `${sign}${formatPercent(num, 3)}`);
    },
    sortable: true,
    sortAccessor: (row: PortfolioRow) => {
      if (row.targetWeightPct == null && row.currentWeightPct == null) return 0;
      if (row.currentWeightPct == null && row.shares !== 0) return 0;
      return (row.targetWeightPct ?? 0) - (row.currentWeightPct ?? 0);
    },
    align: "right",
    tooltip: "Target % minus Actual %",
    isDiffColumn: true,
    dataSource: "hybrid",
  },

  targetQty: {
    id: "targetQty",
    label: "Target Qty",
    accessor: (row: PortfolioRow) => row.targetQty,
    formatter: value => (value !== null && value !== undefined ? formatNumber(value as number) : "-"),
    sortable: false,
    align: "right",
    isDiffColumn: true,
    filterable: true,
    filterMode: "nonzero",
    dataSource: "live",
  },

  rawTargetQty: {
    id: "rawTargetQty",
    label: "Raw Qty",
    accessor: (row: PortfolioRow) => row.rawTargetQty,
    formatter: value => (value !== null && value !== undefined ? formatNumber(value as number) : "-"),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.rawTargetQty ?? 0,
    align: "right",
    tooltip: "Target quantity before lot size rounding",
    isDiffColumn: true,
    filterable: true,
    filterMode: "nonzero",
    dataSource: "live",
  },

  effectiveTargetQty: {
    id: "effectiveTargetQty",
    label: "Effective Qty",
    accessor: (row: PortfolioRow) => row.effectiveTargetQty,
    formatter: value => (value !== null && value !== undefined ? formatNumber(value as number) : "-"),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.effectiveTargetQty ?? 0,
    align: "right",
    tooltip: "Projected position after lot-rounded trade executes",
    isDiffColumn: true,
    filterable: false,
    dataSource: "live",
  },

  tradeQty: {
    id: "tradeQty",
    label: "Trade Qty",
    accessor: (row: PortfolioRow) => row.tradeQty,
    formatter: value => formatTradeQty(value as number | null),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.tradeQty ?? 0,
    align: "right",
    isDiffColumn: true,
    filterable: true,
    filterMode: "nonzero",
    dataSource: "hybrid",
    tooltip:
      "Unassigned: Live calc = (targetWeight% * netLiq / price) - currentQty. " +
      "Assigned: Snapshot from stage_assigned diff (recorded at assignment time). " +
      "Positive = BUY, Negative = SELL.",
  },

  tradeValue: {
    id: "tradeValue",
    label: "Trade Value",
    accessor: (row: PortfolioRow) => row.tradeValue,
    formatter: (value, row) =>
      value !== null && value !== undefined ? formatCurrency(value as number, row.currency) : "-",
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.tradeValue ?? 0,
    align: "right",
    isDiffColumn: true,
    filterable: true,
    filterMode: "nonzero",
    dataSource: "live",
    tooltip:
      "Live calculation: Trade Qty x Current Market Price. " +
      "Displayed in native currency. " +
      "Updates in real-time as prices change.",
  },

  tradeValueUsd: {
    id: "tradeValueUsd",
    label: "Trade $",
    accessor: (row: PortfolioRow) => row.tradeValueUsd,
    formatter: (value) =>
      value !== null && value !== undefined ? formatUsd(value as number) : "-",
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.tradeValueUsd ?? 0,
    align: "right",
    isDiffColumn: true,
    filterable: true,
    filterMode: "nonzero",
    dataSource: "live",
    tooltip:
      "Live calculation: Trade Qty x Current Price in USD. " +
      "Converted using current FX rate. " +
      "Use for cross-currency comparisons.",
  },

  fxRate: {
    id: "fxRate",
    label: "FX Rate",
    accessor: (row: PortfolioRow) => row.fxRate,
    formatter: value => formatFxRate(value as number | null, 3),
    sortable: false,
    align: "center",
    tooltip: "FX Rate to USD",
    isDiffColumn: true,
    dataSource: "live",
  },

  minSize: {
    id: "minSize",
    label: "Lot Size",
    accessor: (row: PortfolioRow) => row.minSize,
    formatter: value => (value && (value as number) > 0 ? formatNumber(value as number, 4) : "-"),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.minSize ?? 0,
    align: "right",
    tooltip: "Minimum trading lot size",
    isDiffColumn: true,
    dataSource: "live",
  },

  // ============================================================================
  // Trade Action Column (1) - BUY/SELL/HOLD badge
  // ============================================================================

  tradeAction: {
    id: "tradeAction",
    label: "Action",
    accessor: (row: PortfolioRow) => row.tradeAction,
    // Formatter returns the raw value - component will render as badge
    formatter: value => (value as string) || "-",
    sortable: true,
    align: "center",
    isDiffColumn: true,
    filterable: true,
    filterOptions: () => ["BUY", "SELL", "HOLD"],
    dataSource: "live",
  },

  // ============================================================================
  // Stage/Execution Columns (9) - shown in rebalance event view
  // ============================================================================

  hold: {
    id: "hold",
    label: "Hold",
    accessor: (row: PortfolioRow) => (row.isHeld ? 1 : 0),
    formatter: value => (value === 1 ? "Yes" : "-"),
    sortable: false,
    align: "center",
    tooltip: "Manual override to exclude position from trading",
    isDiffColumn: true,
    dataSource: "stored",
    // Rendered as checkbox in component
  },

  stage: {
    id: "stage",
    label: "Stage",
    accessor: (row: PortfolioRow) => row.stageName || (row.stageId ? `Stage ${row.stageId}` : "Unassigned"),
    formatter: (value, row) => row.stageName || (row.stageId ? String(row.stageId) : "-"),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.stageId ?? 0,
    align: "center",
    width: "150px",
    filterable: true,
    filterOptions: (rows: PortfolioRow[]) => {
      const stages = new Set<string>();
      stages.add("Unassigned");
      rows.forEach(row => {
        if (row.stageName) stages.add(row.stageName);
      });
      // Sort Stage 1, Stage 2, etc. properly
      return Array.from(stages).sort((a, b) => {
        if (a === "Unassigned") return -1;
        if (b === "Unassigned") return 1;
        return a.localeCompare(b, undefined, { numeric: true });
      });
    },
    tooltip: "Execution stage this trade is assigned to",
    isDiffColumn: true,
    dataSource: "stored",
    // Rendered as dropdown in component
  },

  thresholdStatus: {
    id: "thresholdStatus",
    label: "TH",
    accessor: (row: PortfolioRow) => row.thresholdStatus || "",
    formatter: (value) => (value as string) || "-",
    sortable: true,
    sortAccessor: (row: PortfolioRow) => {
      if (row.thresholdStatus === "Y") return 2;
      if (row.thresholdStatus === "E") return 1;
      return 0;
    },
    align: "center",
    width: "40px",
    tooltip: "Threshold Status: Y=Excluded (below threshold), E=Exempt (position close), blank=Normal",
    filterable: true,
    filterOptions: (rows: PortfolioRow[]) => {
      const values = new Set<string>();
      rows.forEach(r => {
        const status = r.thresholdStatus || "";
        values.add(status);
      });
      return Array.from(values).sort();
    },
    isDiffColumn: true,
    dataSource: "live",
    // Rendered with color coding in TableRow component
  },

  stageQty: {
    id: "stageQty",
    label: "Stage Qty",
    accessor: (row: PortfolioRow) => row.stageQty,
    formatter: value => (value !== null && value !== undefined ? formatNumber(value as number, 3) : "-"),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.stageQty ?? 0,
    align: "right",
    tooltip: "Saved trade quantity from backend (linked to stage)",
    isDiffColumn: true,
    dataSource: "stored",
  },

  drift: {
    id: "drift",
    label: "Drift",
    accessor: (row: PortfolioRow) => {
      if (row.stageQty === null) return "-";
      return row.hasDrift ? "Yes" : "No";
    },
    // Returns numeric value for sorting; component renders icon
    formatter: (value, row) => {
      // stageQty is null means not saved yet - show "-"
      if (row.stageQty === null) return "-";
      // hasDrift true means mismatch - component renders warning icon
      // hasDrift false means match - component renders checkmark
      return row.hasDrift ? "!" : "ok";
    },
    sortable: true,
    sortAccessor: (row: PortfolioRow) => (row.hasDrift ? 1 : 0),
    align: "center",
    tooltip:
      "Pre-execution: Stage Qty vs stage_assigned diff (stage modified since assignment?). " +
      "Post-execution: Stage Qty vs Filled Qty (execution matched plan?). " +
      "Tolerance: 0.0001 for floating point precision.",
    isDiffColumn: true,
    filterable: true,
    filterOptions: () => ["Yes", "No", "-"],
    dataSource: "live",
    // Rendered as icon in component (checkmark or warning)
  },

  stageStatus: {
    id: "stageStatus",
    label: "Stage Status",
    accessor: (row: PortfolioRow) => row.stageState,
    formatter: value => (value as string) || "-",
    sortable: true,
    align: "center",
    filterable: true,
    filterOptions: () => ["created", "ready", "executing", "completed", "failed", "cancelled"],
    tooltip: "Current state of the assigned stage",
    isDiffColumn: true,
    dataSource: "stored",
    // Rendered as badge
  },

  stageWindow: {
    id: "stageWindow",
    label: "Stage Window (UTC)",
    accessor: (row: PortfolioRow) => {
      if (!row.stageStartTime || !row.stageEndTime) return "-";
      const start = new Date(row.stageStartTime);
      const end = new Date(row.stageEndTime);
      const formatTimeUtc = (d: Date) => {
        const hours = d.getUTCHours().toString().padStart(2, "0");
        const minutes = d.getUTCMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}`;
      };
      return `${formatTimeUtc(start)} - ${formatTimeUtc(end)}`;
    },
    formatter: (value, row) => {
      if (!row.stageStartTime || !row.stageEndTime) return "-";
      const start = new Date(row.stageStartTime);
      const end = new Date(row.stageEndTime);
      const formatTimeUtc = (d: Date) => {
        const hours = d.getUTCHours().toString().padStart(2, "0");
        const minutes = d.getUTCMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}`;
      };
      return `${formatTimeUtc(start)} - ${formatTimeUtc(end)}`;
    },
    sortable: true,
    sortAccessor: (row: PortfolioRow) => (row.stageStartTime ? new Date(row.stageStartTime).getTime() : 0),
    align: "center",
    tooltip: "Trading time window for this stage (UTC)",
    isDiffColumn: true,
    filterable: true,
    filterOptions: (rows: PortfolioRow[]) => {
      const windows = new Set<string>();
      rows.forEach(row => {
        if (!row.stageStartTime || !row.stageEndTime) {
          windows.add("-");
        } else {
          const start = new Date(row.stageStartTime);
          const end = new Date(row.stageEndTime);
          const formatTimeUtc = (d: Date) => {
            const hours = d.getUTCHours().toString().padStart(2, "0");
            const minutes = d.getUTCMinutes().toString().padStart(2, "0");
            return `${hours}:${minutes}`;
          };
          windows.add(`${formatTimeUtc(start)} - ${formatTimeUtc(end)}`);
        }
      });
      return Array.from(windows).sort();
    },
    dataSource: "stored",
  },

  orderId: {
    id: "orderId",
    label: "Order ID",
    accessor: (row: PortfolioRow) => row.orderId || "-",
    formatter: value => (value as string) || "-",
    sortable: true,
    align: "left",
    tooltip: "Broker order ID after execution",
    isDiffColumn: true,
    filterable: true,
    filterOptions: (rows: PortfolioRow[]) => {
      const ids = new Set<string>();
      ids.add("-");
      rows.forEach(row => {
        if (row.orderId) ids.add(row.orderId);
      });
      return Array.from(ids).sort();
    },
    dataSource: "stored",
  },

  orderStatus: {
    id: "orderStatus",
    label: "Order Status",
    accessor: (row: PortfolioRow) => row.orderStatus,
    formatter: value => (value as string) || "-",
    sortable: true,
    align: "center",
    filterable: true,
    filterOptions: (rows: PortfolioRow[]) => {
      const statuses = new Set<string>();
      rows.forEach(row => {
        if (row.orderStatus) {
          statuses.add(row.orderStatus);
        }
      });
      return Array.from(statuses).sort();
    },
    tooltip: "Current broker order status",
    isDiffColumn: true,
    dataSource: "stored",
    // Rendered as badge
  },

  filledQty: {
    id: "filledQty",
    label: "Filled Qty",
    accessor: (row: PortfolioRow) => row.filledQty,
    formatter: value => (value !== null && value !== undefined ? formatNumber(value as number, 4) : "-"),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.filledQty ?? 0,
    align: "right",
    tooltip: "Quantity filled by broker",
    isDiffColumn: true,
    dataSource: "stored",
  },

  avgFillPrice: {
    id: "avgFillPrice",
    label: "Avg Price",
    accessor: (row: PortfolioRow) => row.avgFillPrice,
    formatter: (value, row) =>
      value !== null && value !== undefined ? formatPrice(value as number, row.currency) : "-",
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.avgFillPrice ?? 0,
    align: "right",
    tooltip: "Average execution price",
    isDiffColumn: true,
    dataSource: "stored",
  },

  commission: {
    id: "commission",
    label: "Commission",
    accessor: (row: PortfolioRow) => row.commission,
    formatter: value => (value !== null && value !== undefined ? formatUsd(value as number) : "-"),
    sortable: true,
    sortAccessor: (row: PortfolioRow) => row.commission ?? 0,
    align: "right",
    tooltip: "Total broker commission paid",
    isDiffColumn: true,
    dataSource: "stored",
  },
};

/**
 * Get a column definition by ID.
 * Throws if column doesn't exist.
 */
export function getColumnDefinition(id: string): ColumnDefinition {
  const column = columnDefinitions[id];
  if (!column) {
    throw new Error(`Unknown column ID: ${id}`);
  }
  return column;
}

/**
 * Get multiple column definitions by ID array.
 * Maintains the order of the input array.
 */
export function getColumnDefinitions(ids: string[]): ColumnDefinition[] {
  return ids.map(getColumnDefinition);
}

/**
 * All column IDs for reference
 */
export const COLUMN_IDS = {
  // Core
  SYMBOL: "symbol",
  CURRENCY: "currency",
  SHARES: "shares",
  QTY: "qty",
  AVG_COST: "avgCost",
  COST_BASIS: "costBasis",
  COST_BASIS_LOCAL: "costBasisLocal",
  MKT_PRICE: "mktPrice",
  PRICE_USD: "priceUsd",
  MKT_VALUE: "mktValue",
  MKT_VALUE_LOCAL: "mktValueLocal",
  MKT_VALUE_USD: "mktValueUsd",
  REALIZED_PNL: "realizedPnl",
  UNREALIZED_PNL: "unrealizedPnl",
  UNREALIZED_PNL_USD: "unrealizedPnlUsd",
  GROUP: "group",
  SECTOR: "sector",
  CON_ID: "conId",
  EXCLUDED: "excluded",
  ISSUES: "issues",
  // Expanded
  SEC_TYPE: "secType",
  // Diff
  CURRENT_WEIGHT_PCT: "currentWeightPct",
  TARGET_WEIGHT_PCT: "targetWeightPct",
  TARGET_QTY: "targetQty",
  RAW_TARGET_QTY: "rawTargetQty",
  EFFECTIVE_TARGET_QTY: "effectiveTargetQty",
  TRADE_QTY: "tradeQty",
  TRADE_VALUE: "tradeValue",
  TRADE_VALUE_USD: "tradeValueUsd",
  FX_RATE: "fxRate",
  MIN_SIZE: "minSize",
  TRADE_ACTION: "tradeAction",
  // Stage/Execution
  HOLD: "hold",
  STAGE: "stage",
  THRESHOLD_STATUS: "thresholdStatus",
  STAGE_QTY: "stageQty",
  DRIFT: "drift",
  STAGE_STATUS: "stageStatus",
  STAGE_WINDOW: "stageWindow",
  ORDER_ID: "orderId",
  ORDER_STATUS: "orderStatus",
  FILLED_QTY: "filledQty",
  AVG_FILL_PRICE: "avgFillPrice",
  COMMISSION: "commission",
} as const;
